import classNames from 'classnames'
import i18next from 'i18next'
import React, { createContext, ReactElement, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { IconFa } from '../icon-fa'
import styles from './index.module.css'

export const ModalPortalContext = createContext<HTMLElement | null>(null)

export const useModalPortalContainer = (): HTMLElement => {
  const container = useContext(ModalPortalContext)
  return container ?? document.body
}

export type ModalSize = 'small' | 'medium' | 'large' | 'xl'
export interface ModalProps {
  onClose: () => void
  open?: boolean
  className?: string
  title?: ReactNode
  'aria-title'?: string
  description?: ReactNode
  initialFocus?: React.MutableRefObject<HTMLButtonElement | null>
  modalBackgroundClassName?: string
  modalContainerClassName?: string
  modalContentClassName?: string
  children: ReactNode
  noPaddingBelowDescription?: boolean
  scrollable?: boolean
  icon?: ReactNode
  bottomPadding?: boolean
  size?: ModalSize
  rightContent?: ReactNode
  hideCloseButton?: boolean
}

export function NativeModalBase({
  size = 'small',
  scrollable = false,
  open = true,
  ...props
}: ModalProps): ReactElement {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open) {
      if (!dialog.open) {
        dialog.showModal()
      }
      if (props.initialFocus?.current) {
        props.initialFocus.current.focus()
      }
    } else {
      dialog.close()
    }
  }, [open, props.initialFocus])

  const handleCancel = (e: React.SyntheticEvent) => {
    e.preventDefault()
    props.onClose()
  }

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      aria-label={props['aria-title']}
      className={classNames('p-0 backdrop:bg-black/20 rounded-lg', props.modalBackgroundClassName)}
    >
      <ModalPortalContext.Provider value={portalContainer}>
        <div
          ref={setPortalContainer}
          className="fixed inset-0 flex items-center w-screen justify-center min-h-screen text-base p-4"
          onPointerDown={(e) => {
            return e.target === e.currentTarget && props.onClose()
          }}
        >
          <div
            className={classNames(
              props.className,
              styles.modalContainer,
              props.modalContainerClassName,
              styles[size],
              props.bottomPadding && 'pb-4',
              scrollable && 'overflow-y-auto'
            )}
          >
            {!props.hideCloseButton && (
              <div className="flex justify-end sticky top-0 right-0 z-10 bg-white rounded-xs">
                <button
                  type="button"
                  aria-label={i18next.t('glow.close')}
                  onClick={props.onClose}
                  className="p-0 cursor-pointer bg-transparent border-none w-11 h-10 sm:h-11 font-bold text-base text-emphasis-extra"
                >
                  <IconFa icon={['far', 'times']} title={i18next.t('glow.close')} />
                </button>
              </div>
            )}
            <div className={classNames('px-6 sm:px-8', props.modalContentClassName)}>
              {props.title || props.icon ? (
                <div className="flex justify-between">
                  <div className={classNames('flex flex-col gap-2', props.noPaddingBelowDescription ? '' : 'pb-4')}>
                    <h5 className="m-0 text-lg text-emphasis-high font-semibold">
                      {props.icon}
                      {props.title}
                    </h5>
                    {props.description && (
                      <span className="m-0 text-base text-emphasis-medium font-normal">{props.description}</span>
                    )}
                  </div>
                  <div>{props.rightContent}</div>
                </div>
              ) : null}
              {props.children}
            </div>
          </div>
        </div>
      </ModalPortalContext.Provider>
    </dialog>
  )
}
